// Pure helpers for broadcast recipient fields on the client. A recipient
// field holds one explicit value per recipient (contrast with the old
// interchangeable code-list pools): these helpers support the coverage
// tally, duplicate-value tinting, and CSV import matching in
// RecipientFieldDialog.

const KEY_FORMAT = /^[a-z][a-z0-9_]*$/;

// Converts a recipient field name into a lowercase snake_case key that
// satisfies the backend format validation (/\A[a-z][a-z0-9_]*\z/).
export const slugifyKey = name => {
  const slug = (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (slug.length === 0) {
    return 'recipient_field';
  }

  const key = /^[a-z]/.test(slug) ? slug : `field_${slug}`;

  return KEY_FORMAT.test(key) ? key : 'recipient_field';
};

// How many recipients still don't have a value for this field.
export const missingCount = (filledCount, recipientCount) =>
  Math.max((recipientCount || 0) - (filledCount || 0), 0);

// Recipient fields only matter once their token actually shows up in one of
// the message bodies being composed/sent. Bodies may be undefined/null (e.g.
// an SMS-only broadcast has no emailBody).
export const referencedRecipientFields = (fields, ...bodies) => {
  const haystack = bodies.filter(Boolean).join('\n');

  return (fields || []).filter(
    field => field.token && haystack.includes(field.token),
  );
};

// Given the table rows in display order (`{ userUuid, value }`), returns a
// map of userUuid -> the userUuid of the first row that already used the
// same (trimmed, non-blank) value. Lets the table tint duplicate rows and
// show "duplicate of <name>" immediately, without a round trip to the
// backend (which computes `duplicateValues` on save).
export const computeDuplicateOf = (rows = []) => {
  const seen = new Map();
  const duplicateOf = {};

  rows.forEach(({ userUuid, value }) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;

    if (seen.has(trimmed)) {
      duplicateOf[userUuid] = seen.get(trimmed);
    } else {
      seen.set(trimmed, userUuid);
    }
  });

  return duplicateOf;
};

const HEADER_ALIASES = {
  employeeId: ['employee id', 'employee_id', 'employeeid', 'employee number'],
  email: ['email', 'email address', 'e-mail'],
  phone: [
    'phone',
    'phone number',
    'phone_number',
    'mobile',
    'mobile number',
    'cell',
    'cell phone',
  ],
  value: ['value', 'code'],
};

const normalizeHeader = cell => (cell || '').trim().toLowerCase();

// Classifies a header cell as one of the known columns, or null. The value
// column is also recognized by the field's own name (e.g. "Gift card code")
// or any header containing "code", so a client can append a column named
// after the field to the eligibility-file layout they already use.
const headerKind = (cell, fieldName) => {
  const text = normalizeHeader(cell);
  const known = Object.keys(HEADER_ALIASES).find(kind =>
    HEADER_ALIASES[kind].includes(text),
  );

  if (known) return known;
  if (fieldName && text === normalizeHeader(fieldName)) return 'value';
  if (text.includes('code')) return 'value';

  return null;
};

const looksLikeHeaderRow = (cells, fieldName) =>
  cells.some(cell => headerKind(cell, fieldName));

// A phone-looking identifier: only digits, spaces, +, parentheses, dots and
// dashes, with at least 10 digits. Employee IDs like "EW-10422" have letters
// and fall through to the employee-ID branch.
const looksLikePhone = text =>
  /^[+(\d][\d\s().-]*$/.test(text) && text.replace(/\D/g, '').length >= 10;

const classifyIdentifier = identifier => {
  const text = (identifier || '').trim();

  if (!text) return {};
  if (text.includes('@')) return { email: text };
  if (looksLikePhone(text)) return { phone: text };

  return { employeeId: text };
};

// Under a named identifier column the content still wins when it is
// unmistakable (an "@" means email, phone punctuation means phone), so a
// file whose only header is "employee id" can mix all three identifiers.
// A plain run of digits under "employee id" stays an employee ID.
const classifyUnderHeader = (kind, cell) => {
  if (cell.includes('@')) return { email: cell };
  if (looksLikePhone(cell) && (kind !== 'employeeId' || /\D/.test(cell))) {
    return { phone: cell };
  }

  return { [kind]: cell };
};

const emptyRow = () => ({
  employeeId: null,
  email: null,
  phone: null,
  value: '',
});

const IDENTIFIER_KINDS = ['employeeId', 'email', 'phone'];

// Splits one CSV/TSV line into trimmed cells. Quoted fields may contain the
// delimiter and doubled quotes ("" -> "), as exported by Excel and Sheets.
const splitLine = line => {
  const delimiter = line.includes('\t') ? '\t' : ',';
  const cells = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && cell.trim() === '') {
      quoted = true;
      cell = '';
    } else if (char === delimiter) {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim());

  return cells;
};

const assignIdentifier = (row, identifier) =>
  Object.entries(identifier).reduce(
    (acc, [key, cell]) => (acc[key] ? acc : { ...acc, [key]: cell }),
    row,
  );

// Builds a row from a header-described line. Named identifier columns are
// read by content-aware kind; when the header names no identifier column at
// all (e.g. "who,value"), the first non-value column is the identifier and
// is classified purely by content, like a headerless file. Other unnamed
// columns (Full Name, Segment, ...) only ever contribute an email or phone
// they visibly contain, never an employee ID.
const rowFromHeader = (kinds, valueIndex, cols) => {
  const hasIdentifierColumn = kinds.some(kind =>
    IDENTIFIER_KINDS.includes(kind),
  );
  const fallbackIndex = kinds.findIndex((_, index) => index !== valueIndex);

  const row = kinds.reduce((acc, kind, index) => {
    const cell = cols[index];
    if (index === valueIndex || !cell) return acc;

    if (IDENTIFIER_KINDS.includes(kind)) {
      return assignIdentifier(acc, classifyUnderHeader(kind, cell));
    }
    if (!hasIdentifierColumn && index === fallbackIndex) {
      return assignIdentifier(acc, classifyIdentifier(cell));
    }
    if (cell.includes('@') || looksLikePhone(cell)) {
      return assignIdentifier(acc, classifyIdentifier(cell));
    }

    return acc;
  }, emptyRow());

  return { ...row, value: cols[valueIndex] || '' };
};

// Parses pasted/uploaded CSV (or TSV) text into
// `{ employeeId, email, phone, value }` rows for
// BroadcastRecipientFields::MatchRows. The header row is optional:
// - With a recognized header ("employee id" / "email" / "phone" / "value" or
//   "code", case-insensitive), columns are read by name, so a file can carry
//   several identifiers per row (e.g. employee id, phone, value). When no
//   value column is named, the last column is the value.
// - Without a header, column 1 is the identifier (an email when it contains
//   "@", a phone when it is 10+ digits with only phone punctuation, otherwise
//   an employee ID) and column 2 is the value.
export const parseCsvRows = (raw, fieldName = null) => {
  const lines = (raw || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length === 0) return [];

  const table = lines.map(splitLine);
  const hasHeader = looksLikeHeaderRow(table[0], fieldName);
  const dataRows = hasHeader ? table.slice(1) : table;

  if (hasHeader) {
    const kinds = table[0].map(cell => headerKind(cell, fieldName));
    const valueIndex = kinds.includes('value')
      ? kinds.indexOf('value')
      : kinds.length - 1;

    return dataRows
      .filter(cols => cols.some(cell => cell))
      .map(cols => rowFromHeader(kinds, valueIndex, cols));
  }

  return dataRows
    .filter(cols => cols.length >= 2 && (cols[0] || cols[1]))
    .map(([identifier, value]) => ({
      ...emptyRow(),
      ...classifyIdentifier(identifier),
      value: value || '',
    }));
};

const csvCell = value => {
  const text = value == null ? '' : String(value);

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Builds the CSV an admin can download from the Import CSV tab. The columns
// mirror the eligibility file template clients already know (Employee ID,
// Full Name, Email, Phone Number), followed by a column named after the field
// left empty, so the client only has to fill in the values and import it back.
export const buildCsvTemplate = (recipientRows = [], fieldName = 'Value') => {
  const header = [
    'Employee ID',
    'Full Name',
    'Email',
    'Phone Number',
    fieldName,
  ]
    .map(csvCell)
    .join(',');
  const rows = recipientRows.map(row =>
    [row.employeeId, row.name, row.email, row.phone, ''].map(csvCell).join(','),
  );

  return [header, ...rows].join('\n') + '\n';
};
